
import React from 'react';
import { Milestone, ScenarioObjective, Scenario } from '../../types';
import { playUiSfx } from '../../utils/uiAudio';

interface ObjectiveCardProps {
    milestones: Milestone[];
    scenarioObjectives: ScenarioObjective[];
    activeScenario?: Scenario;
    onAskAICoach?: (objective: string) => void;
    onAutoSolve?: () => void;
}

const ObjectiveCard: React.FC<ObjectiveCardProps> = ({ milestones, scenarioObjectives, activeScenario, onAskAICoach, onAutoSolve }) => {
    let title = "Next Objective";
    let desc = "All objectives complete.";
    let reward = 0;
    let currentTip: string | undefined;
    
    // Determine what to show
    if (activeScenario) {
        title = activeScenario.title;
        // Find first incomplete objective index to get corresponding tip
        const firstIncompleteIdx = scenarioObjectives.findIndex(o => !o.completed);
        
        if (firstIncompleteIdx !== -1) {
            currentTip = activeScenario.tips?.[firstIncompleteIdx];
        }
    } else {
        const nextMilestone = milestones.find(m => !m.completed);
        if (nextMilestone) {
            desc = nextMilestone.name;
            reward = nextMilestone.reward;
        }
    }

    const isTutorial = activeScenario?.id.startsWith('tut_');

    return (
        <div className="w-72 bg-black/60 text-white p-4 rounded-xl border border-indigo-500/30 shadow-xl backdrop-blur-md flex flex-col justify-center animate-slide-in-left relative overflow-hidden group pointer-events-auto">
             {/* Header */}
             <div className="flex justify-between items-center mb-3 pb-2 border-b border-white/10">
                 <div className="text-[10px] uppercase font-bold text-indigo-400 tracking-widest">{title}</div>
                 <div className="flex items-center gap-1.5">
                     {activeScenario && onAskAICoach && !scenarioObjectives.every(o => o.completed) && (
                         <button
                             type="button"
                             data-testid="objectives-ai-coach"
                             onClick={() => {
                                 playUiSfx('click');
                                 const activeObj = scenarioObjectives.find(o => !o.completed);
                                 if (activeObj) onAskAICoach(activeObj.description);
                             }}
                             className="text-[8px] bg-indigo-600 hover:bg-indigo-500 text-white px-2 py-0.5 rounded uppercase font-bold tracking-wide transition-colors"
                             title="Ask AI Coach about the current objective"
                         >
                             AI Coach
                         </button>
                     )}
                     {activeScenario && <div className="text-[9px] bg-red-500/20 text-red-300 px-1.5 rounded border border-red-500/30 font-mono">OP-LIVE</div>}
                 </div>
             </div>

             {/* Scenario Checklist Mode */}
             {activeScenario ? (
                 <div className="space-y-2 mb-2">
                     {scenarioObjectives.map((obj, i) => {
                         const isActive = !obj.completed && scenarioObjectives.slice(0,i).every(o => o.completed);
                         return (
                            <div key={i} className={`flex items-start gap-2 text-xs ${obj.completed ? 'text-emerald-400 opacity-60 line-through' : isActive ? 'text-white font-bold' : 'text-slate-400'}`}>
                                <span className="mt-0.5">{obj.completed ? '✓' : '☐'}</span>
                                <span>{obj.description}</span>
                            </div>
                         );
                     })}
                 </div>
             ) : (
                 /* Sandbox Milestone Mode */
                 <>
                    <div className="font-bold text-sm text-slate-100 leading-snug mb-2 font-mono">{desc}</div>
                    {reward > 0 && (
                        <div className="text-[10px] text-emerald-400 font-mono flex items-center gap-1">
                            <span className="text-emerald-600">Reward:</span> ${reward.toLocaleString()}
                        </div>
                    )}
                 </>
             )}
             
             {/* Tutorial Hint Box */}
             {activeScenario && currentTip && !scenarioObjectives.every(o => o.completed) && (
                 <div className="mt-3 bg-indigo-900/20 p-2 rounded border border-indigo-500/30">
                     <div className="flex justify-between items-center mb-1">
                         <span className="text-[9px] font-bold uppercase text-indigo-300 tracking-wider">Tactical Tip</span>
                         <div className="flex gap-1">
                            {/* Auto-Deploy Button (Tutorial Only) */}
                            {isTutorial && onAutoSolve && (
                                <button 
                                    type="button"
                                    data-testid="objectives-auto-deploy"
                                    onClick={() => { playUiSfx('build'); onAutoSolve(); }}
                                    className="text-[8px] bg-emerald-600 hover:bg-emerald-500 text-white px-2 py-0.5 rounded uppercase font-bold tracking-wide transition-colors flex items-center gap-1"
                                    title="Auto-build required structures"
                                >
                                    <span>Auto-Deploy</span>
                                </button>
                            )}
                            
                            {/* AI Coach Button */}
                            {isTutorial && onAskAICoach && (
                                <button 
                                    onClick={() => { 
                                        playUiSfx('click'); 
                                        const activeObj = scenarioObjectives.find(o => !o.completed);
                                        if(activeObj) onAskAICoach(activeObj.description); 
                                    }}
                                    className="text-[8px] bg-indigo-600 hover:bg-indigo-500 text-white px-2 py-0.5 rounded uppercase font-bold tracking-wide transition-colors flex items-center gap-1"
                                    title="Ask Gemini for an explanation"
                                >
                                    <span>Ask AI Coach</span>
                                </button>
                            )}
                         </div>
                     </div>
                     <p className="text-[10px] text-indigo-100 leading-relaxed italic">
                         "{currentTip}"
                     </p>
                 </div>
             )}
        </div>
    );
};

export default ObjectiveCard;
